import React, { useEffect, useMemo, useRef } from 'react';
import { GetStaticProps } from 'next';
import { setFaroSessionAttribute } from '@utils/faro';
import { useRouter } from 'next/router';
import {
  CheckoutComMachineProvider,
  useFeatureIsOn,
  useCheckoutCartId,
} from '@monofront/business/contexts';
import {
  formatAbTestDocumentData,
  formatPrismicData,
} from '@monofront/business/utils';
import { ICheckoutPageContent } from '@monofront/types';

import PaypalProvider from '@context/PaypalProvider';
import ApplePayProvider from '@context/ApplePayProvider';
import withAuthentication from '@hocs/withAuthentication';
import { ICheckoutSteps } from '@hooks/useCheckoutSteps';
import getCookieConsent from '@utils/getCookieConsent';
import { LFPage, injectLayoutData } from '@utils/layout';
import { prismicService } from '@services/businessContainer';

import CheckoutPayment from '@screens/CheckoutPayment';
import {
  CheckoutPaymentProvider,
  CheckoutPaymentFlowProvider,
} from '@screens/CheckoutPayment/contexts';
import { useCheckoutFlowEnabledForCheckout } from '@hooks/useCheckoutFlowEnabled';
import { PaymentMethodProvider } from '@features/PaymentMethods/PaymentMethodContext';
import createLayoutCheckout from '@components/LayoutCheckout';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import { sendMessageToMobile } from '@utils/mobile';
import {
  useEffectOnce,
  useSubscribeInCheckout,
} from '@monofront/business/hooks';
import {
  CheckoutSelectAddressMachineProvider,
  useCheckoutCartMachine,
} from '@context/checkoutCartMachineContext';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { FundingSources } from '@monofront/business/constants';
import HeartLoader from '@monofront/design-system/components/HeartLoader/HeartLoader';
import { useAuthentication } from '@context/businessContainer';

interface IPaymentPageProps {
  pageContents?: Partial<ICheckoutPageContent>;
}

const PaymentPage: {
  getLayout: () => void;
  hasAppMobileLayout: boolean;
} & LFPage<IPaymentPageProps> = ({ pageContents }) => {
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  // Same A/B flag that branches the layout inside CheckoutPayment. Frozen to
  // its first-render value: useFeatureIsOn re-evaluates live, and a later
  // GrowthBook attribute update (e.g. auth/customer data resolving after
  // mount) can flip it, which would swap PaymentProvider's component identity
  // and force React to unmount/remount the whole subtree mid-session, losing
  // any in-progress payment selection. The machine-backed provider serves the
  // Flow layout; the legacy useState provider serves Frames. Drop the Frames
  // branch + provider when the test ends.
  const isCheckoutFlowEnabledLive = useCheckoutFlowEnabledForCheckout();
  const isCheckoutFlowEnabledRef = useRef(isCheckoutFlowEnabledLive);
  const PaymentProvider = isCheckoutFlowEnabledRef.current
    ? CheckoutPaymentFlowProvider
    : CheckoutPaymentProvider;
  const isVariant = useFeatureIsOn(pageContents?.featureFlagKey || '');
  const checkoutPageContent = useMemo(
    () =>
      formatAbTestDocumentData(
        pageContents,
        isVariant,
      ) as Partial<ICheckoutPageContent>,
    [pageContents, isVariant],
  );

  const router = useRouter();
  const { shoppingCart } = useShoppingCart();
  const [checkoutCartId] = useCheckoutCartId();
  const { startCheckout, canStartCheckout, canStartOrRestartCheckout } =
    useCheckoutCartMachine();

  useEffect(() => {
    // If the user consents to performances cookies
    if (getCookieConsent(OneTrustCookieCategories.ANALYTICS)) {
      setFaroSessionAttribute('checkoutCartId', checkoutCartId);
    }
  }, [checkoutCartId]);

  useEffectOnce(
    () => shoppingCart.products && canStartCheckout(),
    () => {
      startCheckout(shoppingCart.products);
    },
    [shoppingCart.products],
  );

  if (!canStartOrRestartCheckout()) {
    return <HeartLoader />;
  }

  return (
    <CheckoutSelectAddressMachineProvider>
      <CheckoutComMachineProvider
        sendMessageToMobile={sendMessageToMobile}
        // Both Flow exit paths converge on /checkout/processing: this one (no
        // 3DS) and the CKO redirect, whose success_url lego points there too.
        onFlowPaymentCompleted={(paymentId) =>
          router.push({
            pathname: '/checkout/processing',
            query: { checkoutCartId, 'cko-payment-id': paymentId },
          })
        }
      >
        <PaypalProvider
          namespace="payment-page"
          options={
            isSubscriptionAvailable
              ? {
                  vault: true,
                  components: 'buttons',
                }
              : {
                  components: 'buttons,funding-eligibility',
                  enableFunding: FundingSources.PAY_LATER,
                }
          }
        >
          <ApplePayProvider>
            <PaymentMethodProvider>
              <PaymentProvider>
                <CheckoutPayment pageContents={checkoutPageContent} />
              </PaymentProvider>
            </PaymentMethodProvider>
          </ApplePayProvider>
        </PaypalProvider>
      </CheckoutComMachineProvider>
    </CheckoutSelectAddressMachineProvider>
  );
};

PaymentPage.getLayout = () => createLayoutCheckout(ICheckoutSteps.PAYMENT);
PaymentPage.hasAppMobileLayout = true;

export const getStaticProps = injectLayoutData<
  IPaymentPageProps,
  GetStaticProps<IPaymentPageProps>
  // FIXME: fixed in !1994
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
>(async ({ previewData, locale }) => {
  const response = await prismicService.getDocumentsByCustomTypeName(
    'checkout',
    previewData,
    locale ? { lang: `${locale}-${locale}` } : {},
  );

  const pageContents = response?.results[0]?.data
    ? formatPrismicData(response.results[0].data)
    : null;

  return {
    props: {
      pageContents,
    },
    revalidate: 1800, // 30min
  };
});

export default withAuthentication(PaymentPage);
